import { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Container, Item, PortalContent, Title } from './styles';
import ButtonIcon from 'components/buttonIcon';
import Radio from 'components/radio';

export interface SortTableProps {
  name: string;
  id?: string;
  title?: string;
  items: SortItemsProps[];
  position?: 'left' | 'right';
  onSelect: ({ name, value }: { name: string; value: string }) => void;
}

export interface SortItemsProps {
  value: string;
  label: string;
  selected: boolean;
}

const DROPDOWN_MIN_WIDTH = 240;
const VIEWPORT_MARGIN = 8;

function computeLeft(rect: DOMRect, position: 'left' | 'right'): number {
  const rawLeft = position === 'right' ? rect.right - DROPDOWN_MIN_WIDTH : rect.left;

  const clamped = Math.max(
    VIEWPORT_MARGIN,
    Math.min(rawLeft, window.innerWidth - DROPDOWN_MIN_WIDTH - VIEWPORT_MARGIN)
  );

  return clamped + window.scrollX;
}

export const SortTable = (props: SortTableProps): JSX.Element => {
  const { name, title, id, items, onSelect, position = 'left' } = props;
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnWrapRef = useRef<HTMLSpanElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const onClick = () => {
    if (!open && btnWrapRef.current) {
      const rect = btnWrapRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: computeLeft(rect, position),
      });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!btnWrapRef.current?.contains(target) && !portalRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [open]);

  const handleOnSelect = ({ value }) => {
    setOpen(false);
    onSelect({ name, value });
  };

  return (
    <Container>
      <span ref={btnWrapRef} style={{ display: 'inline-flex' }}>
        <ButtonIcon
          aria-label={title || 'Sort'}
          style={{ margin: '0px 4px', minWidth: '30px' }}
          $terciary
          size={'small'}
          icon={'Sort'}
          onClick={onClick}
        />
      </span>
      {open &&
        ReactDOM.createPortal(
          <PortalContent ref={portalRef} style={{ top: coords.top, left: coords.left }}>
            {title && <Title>{title}</Title>}
            {items.map((item, i) => (
              <Item key={i}>
                <Radio
                  name={id}
                  value={item.value}
                  label={item.label}
                  checked={item.selected}
                  onChange={() => handleOnSelect({ value: item.value })}
                  controlled
                />
              </Item>
            ))}
          </PortalContent>,
          document.body
        )}
    </Container>
  );
};

export default SortTable;
